import {
	ApplicationRef,
	ComponentRef,
	EnvironmentInjector,
	EnvironmentProviders,
	Injectable,
	NgZone,
	Renderer2,
	RendererFactory2,
	createComponent,
	inject,
	provideAppInitializer
} from '@angular/core';
import { CommonColors, tooltipController, TooltipOptions } from '@inscomponents/core';
import { SklTooltipComponent, TooltipData, TooltipPosition } from './tooltip.component';

interface InterceptedTooltipOptions extends Partial<TooltipOptions> {
	target?: HTMLElement;
	event?: { target?: EventTarget | null };
	content?: string;
	componentProps?: Record<string, unknown>;
	color?: CommonColors;
}

export function provideGlobalTooltip(): EnvironmentProviders {
	return provideAppInitializer(() => {
		inject(GlobalTooltipService);
	});
}

@Injectable({ providedIn: 'root' })
export class GlobalTooltipService {
	readonly #ngZone = inject(NgZone);
	readonly #appRef = inject(ApplicationRef);
	readonly #envInjector = inject(EnvironmentInjector);
	readonly #rendererFactory = inject(RendererFactory2);
	readonly #renderer: Renderer2 = this.#rendererFactory.createRenderer(null, null);

	readonly #id = crypto.randomUUID().replaceAll('-', '');
	readonly #tooltipId = `global-tooltip-${this.#id}` as const;

	#currentTarget: HTMLElement | null = null;
	#componentRef: ComponentRef<SklTooltipComponent> | null = null;
	#cleanupLeaveListeners: (() => void) | null = null;

	constructor() {
		this.#interceptWcTooltipController();
		this.#initGlobalListeners();
	}

	clearTooltip() {
		if (this.#currentTarget) {
			this.#currentTarget.removeAttribute('data-has-tooltip');
			const root = this.#currentTarget.getRootNode();
			if (root instanceof ShadowRoot && root.host instanceof HTMLElement) {
				root.host.removeAttribute('data-has-tooltip');
			}
			this.#currentTarget = null;
		}
		if (this.#cleanupLeaveListeners) {
			this.#cleanupLeaveListeners();
			this.#cleanupLeaveListeners = null;
		}
		if (this.#componentRef) {
			const ref = this.#componentRef;
			this.#componentRef = null;
			ref.instance.hide();
			const hostElement = ref.location.nativeElement as HTMLElement;
			const animations = hostElement.getAnimations?.() ?? [];
			const cleanup = () => {
				if (hostElement.parentNode) {
					this.#renderer.removeChild(document.body, hostElement);
				}
				this.#appRef.detachView(ref.hostView);
				ref.destroy();
			};
			if (animations.length) {
				Promise.allSettled(animations.map((a) => a.finished)).then(cleanup);
			} else {
				cleanup();
			}
		}
	}

	#toTrimmedString(value: unknown): string | null {
		if (typeof value !== 'string') return null;
		const trimmed = value.trim();
		return trimmed.length > 0 ? trimmed : null;
	}

	#getEffectiveAnchor(target: HTMLElement): HTMLElement {
		const { width, height } = target.getBoundingClientRect();
		if (width > 0 || height > 0) return target;

		const root = target.getRootNode();
		for (let curr: Node | null = target.parentElement; curr && curr !== root; curr = curr.parentNode) {
			if (curr instanceof HTMLElement) {
				const rect = curr.getBoundingClientRect();
				if (rect.width > 0 || rect.height > 0) return curr;
			}
		}
		if (root instanceof ShadowRoot && root.host instanceof HTMLElement) {
			return root.host;
		}
		return target;
	}

	#createAndShowPopover(target: HTMLElement, content: TooltipData) {
		this.clearTooltip();
		this.#currentTarget = target;

		this.#markTooltipTargets(target);
		this.dismissParentTooltips(target);

		const effectiveAnchor = this.#getEffectiveAnchor(target);
		this.#mountTooltipComponent(content, effectiveAnchor);
		this.#setupLeaveListeners(target, effectiveAnchor);
	}

	#markTooltipTargets(target: HTMLElement) {
		target.setAttribute('data-has-tooltip', '');
		const root = target.getRootNode();
		if (root instanceof ShadowRoot && root.host instanceof HTMLElement) {
			root.host.setAttribute('data-has-tooltip', '');
		}
	}

	isTooltipActive(): boolean {
		return this.#componentRef !== null && this.#currentTarget !== null;
	}

	isTooltipActiveFor(element: HTMLElement): boolean {
		if (!this.#currentTarget || !this.#componentRef) return false;
		let curr: Node | null = this.#currentTarget;
		while (curr && curr !== document) {
			if (curr === element) return true;
			curr = curr.parentNode ?? (curr.getRootNode() instanceof ShadowRoot ? (curr.getRootNode() as ShadowRoot).host : null);
		}
		return false;
	}

	dismissParentTooltips(target: HTMLElement) {
		let curr: Node | null = target.parentNode ?? (target.getRootNode() instanceof ShadowRoot ? (target.getRootNode() as ShadowRoot).host : null);
		while (curr && curr !== document && curr !== document.body) {
			if (curr instanceof HTMLElement && curr.hasAttribute('data-has-tooltip')) {
				curr.dispatchEvent(new PointerEvent('pointerleave', { bubbles: false, pointerType: 'mouse' }));
				curr.dispatchEvent(new MouseEvent('mouseleave', { bubbles: false }));
			}
			curr = curr.parentNode ?? (curr.getRootNode() instanceof ShadowRoot ? (curr.getRootNode() as ShadowRoot).host : null);
		}
	}

	#mountTooltipComponent(content: TooltipData, anchor: HTMLElement) {
		const componentRef = createComponent(SklTooltipComponent, {
			environmentInjector: this.#envInjector,
		});

		componentRef.setInput('content', content);
		componentRef.setInput('id', this.#tooltipId);
		if (content.position) {
			componentRef.setInput('position', content.position);
		}

		this.#appRef.attachView(componentRef.hostView);

		const hostElement = componentRef.location.nativeElement as HTMLElement;
		this.#renderer.appendChild(document.body, hostElement);
		componentRef.changeDetectorRef.detectChanges();

		this.#componentRef = componentRef;
		componentRef.instance.show();
		this.#positionPopoverAt(hostElement, anchor.getBoundingClientRect(), content.position);
	}

	#setupLeaveListeners(target: HTMLElement, anchor: HTMLElement) {
		const onLeave = (event: Event) => {
			const related = (event as PointerEvent).relatedTarget as Node | null;
			if (related && (target.contains(related) || anchor.contains(related))) {
				return;
			}
			this.clearTooltip();
		};

		target.addEventListener('pointerleave', onLeave);
		if (anchor !== target) {
			anchor.addEventListener('pointerleave', onLeave);
		}

		this.#cleanupLeaveListeners = () => {
			target.removeEventListener('pointerleave', onLeave);
			if (anchor !== target) {
				anchor.removeEventListener('pointerleave', onLeave);
			}
		};
	}

	#positionPopoverAt(element: HTMLElement, rect: DOMRect, preferredPosition: TooltipPosition = 'bottom') {
		const padding = 8;
		const gap = 6;
		const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
		const viewportHeight = document.documentElement.clientHeight || window.innerHeight;

		element.style.left = '0px';
		element.style.top = '0px';
		element.style.translate = '0 0';
		element.style.maxWidth = `${viewportWidth - padding * 2}px`;
		element.style.removeProperty('right');
		element.style.removeProperty('bottom');

		const width = element.offsetWidth;
		const height = element.offsetHeight;
		const targetCenterX = rect.left + rect.width / 2;
		const targetCenterY = rect.top + rect.height / 2;

		let x = targetCenterX - width / 2;
		let y = rect.bottom + gap;

		switch (preferredPosition) {
			case 'top':
				y = rect.top - height - gap;
				if (y < padding) y = rect.bottom + gap;
				break;
			case 'left':
			case 'start':
				x = rect.left - width - gap;
				y = targetCenterY - height / 2;
				if (x < padding) x = rect.right + gap;
				break;
			case 'right':
			case 'end':
			case 'side':
				x = rect.right + gap;
				y = targetCenterY - height / 2;
				if (x + width > viewportWidth - padding) x = rect.left - width - gap;
				break;
			default: // 'bottom'
				if (y + height > viewportHeight - padding) y = rect.top - height - gap;
				break;
		}

		x = Math.max(padding, Math.min(x, viewportWidth - width - padding));
		y = Math.max(padding, Math.min(y, viewportHeight - height - padding));

		element.style.left = `${Math.round(x)}px`;
		element.style.top = `${Math.round(y)}px`;
	}

	#initGlobalListeners() {
		this.#ngZone.runOutsideAngular(() => {
			document.addEventListener('pointerdown', () => this.clearTooltip(), { capture: true, passive: true });
			window.addEventListener('scroll', () => this.clearTooltip(), { capture: true, passive: true });
			window.addEventListener('blur', () => this.clearTooltip(), { capture: true, passive: true });
		});
	}

	#interceptWcTooltipController() {
		if (typeof tooltipController?.create !== 'function') return;

		const originalCreate = tooltipController.create.bind(tooltipController);

		tooltipController.create = (opts: TooltipOptions) => {
			const interceptedOpts = (opts ?? {}) as InterceptedTooltipOptions;
			const anchor = this.#extractAnchor(interceptedOpts);
			const text = this.#extractWcTooltipText(interceptedOpts);
			const color = this.#extractWcTooltipColor(interceptedOpts);
			const position = this.#extractWcTooltipPosition(interceptedOpts);

			if (anchor && text) {
				const content: TooltipData = { text, ...(color ? { color } : {}), ...(position ? { position } : {}) };
				return Promise.resolve(this.showTooltip(anchor, content));
			}

			return originalCreate(opts);
		};
	}

	#extractAnchor(opts: InterceptedTooltipOptions): HTMLElement | null {
		const target = opts.anchor ?? opts.target ?? opts.event?.target;
		return target instanceof HTMLElement ? target : null;
	}

	#extractWcTooltipText(opts: InterceptedTooltipOptions): string | null {
		const cp = opts.componentProps;
		const candidates = [opts.text, opts.content, cp?.['text'], cp?.['content'], cp?.['message']];
		for (const candidate of candidates) {
			const text = this.#toTrimmedString(candidate);
			if (text) return text;
		}
		return null;
	}

	#extractWcTooltipColor(opts: InterceptedTooltipOptions): CommonColors | undefined {
		const cp = opts.componentProps;
		const color = opts.color ?? cp?.['color'];
		return typeof color === 'string' ? (color as CommonColors) : undefined;
	}

	#extractWcTooltipPosition(opts: InterceptedTooltipOptions): TooltipPosition | undefined {
		const cp = opts.componentProps;
		const pos = opts.position ?? cp?.['position'];
		return typeof pos === 'string' ? (pos as TooltipPosition) : undefined;
	}

	showTooltip(anchor: HTMLElement, content: TooltipData): HTMLInsTooltipPopupElement {
		this.#ngZone.runOutsideAngular(() => {
			this.clearTooltip();
			this.#createAndShowPopover(anchor, content);
		});

		return {
			present: () => Promise.resolve(),
			dismiss: () => {
				this.clearTooltip();
				return Promise.resolve(true);
			},
			onDidDismiss: () => Promise.resolve(),
		} as unknown as HTMLInsTooltipPopupElement;
	}
}
